import { useState, useEffect, useCallback } from 'react';
import { MCPTask, TaskTreeNode, TaskHierarchyItem } from '../types';
import { apiService, APIError } from '../services/api';
import { useTasks } from './useTasks';
import { useTaskCategories } from './useTaskCategories';

interface UseTasksHierarchyState {
  hierarchy: TaskHierarchyItem[];
  loading: boolean;
  error: APIError | null;
}

export const useTasksHierarchy = () => {
  // Usar hooks independientes para obtener datos
  const {
    tasks,
    loading: tasksLoading,
    error: tasksError,
    refreshTasks,
  } = useTasks();
  const {
    categories,
    loading: categoriesLoading,
    error: categoriesError,
    refreshCategories,
  } = useTaskCategories();

  const [state, setState] = useState<UseTasksHierarchyState>({
    hierarchy: [],
    loading: false,
    error: null,
  });

  // Contador de refresco manual para forzar actualizaciones
  const [refreshCounter, setRefreshCounter] = useState(0);

  // Función para convertir TaskTreeNode del backend a TaskHierarchyItem del frontend
  const transformTreeNodeToHierarchy = (
    node: TaskTreeNode,
    allTasks: MCPTask[]
  ): TaskHierarchyItem => {
    // Buscar la task completa en el array de tasks
    const taskData = allTasks.find(task => task.id === node.id.toString());

    // Si viene del endpoint /categories/tree, es siempre una categoría
    // Solo será task si encontramos datos específicos de task
    const isTask = taskData && taskData.description; // Las tasks tienen descripción

    // Buscar todas las tareas que pertenecen a esta categoría
    const tasksInThisCategory = allTasks.filter(
      task => {
        // Convertir ambos a números para comparar
        const taskCategoryId = typeof task.categoryId === 'string' ? parseInt(task.categoryId) : task.categoryId;
        const nodeCategoryId = typeof node.id === 'string' ? parseInt(node.id) : node.id;

        console.log('🔍 Comparando task categoría:', {
          taskName: task.name,
          taskCategoryId,
          nodeCategoryId,
          match: taskCategoryId === nodeCategoryId,
          categoryName: task.categoryName
        });

        return taskCategoryId === nodeCategoryId ||
          (task.categoryName && task.categoryName === node.name);
      }
    );

    console.log('📋 Tasks encontradas para categoría', node.name, ':', tasksInThisCategory.length);

    // Convertir las tareas a TaskHierarchyItem
    const taskItems: TaskHierarchyItem[] = tasksInThisCategory.map(task => ({
      id: task.id,
      name: task.name,
      type: 'task' as const,
      description: task.description,
      status: task.status,
      createdAt: task.createdAt,
      lastRun: task.lastRun,
      executions: task.executions,
      successRate: task.successRate,
      rubric: parseRubric(task.rubric),
      children: [],
    }));

    // Convertir subcategorías recursivamente
    const subcategoryItems =
      node.children?.map(child =>
        transformTreeNodeToHierarchy(child, allTasks)
      ) || [];

    return {
      id: node.id.toString(),
      name: node.name,
      type: isTask ? 'task' : 'category',
      description: taskData?.description,
      status: taskData?.status || undefined,
      createdAt: taskData?.createdAt,
      lastRun: taskData?.lastRun,
      executions: taskData?.executions || 0,
      successRate: taskData?.successRate || 0,
      rubric: taskData ? parseRubric(taskData.rubric) : undefined,
      children: [...taskItems, ...subcategoryItems], // Combinar tareas y subcategorías
    };
  };

  // Función para parsear la rúbrica desde string YAML a objeto
  const parseRubric = (rubricString: string) => {
    try {
      console.log('🔍 Debug: Parseando rúbrica:', {
        rubricString,
        length: rubricString?.length,
        type: typeof rubricString,
      });

      // Manejo especial para valores de referencia de archivo
      if (
        !rubricString ||
        rubricString.trim() === '' ||
        rubricString === 'default.yml' ||
        rubricString.endsWith('.yml') ||
        rubricString.endsWith('.yaml')
      ) {
        console.log(
          '📄 Rúbrica es referencia de archivo o vacía, creando criterio por defecto'
        );
        return {
          criterios: [
            {
              nombre: 'Criterio por Defecto',
              peso: 100,
              descripcion: 'Evaluación general usando rúbrica estándar',
            },
          ],
        };
      }

      // Si viene como JSON, intentar parsearlo primero
      if (rubricString.startsWith('{') || rubricString.startsWith('[')) {
        try {
          const parsed = JSON.parse(rubricString);
          console.log('📋 Rúbrica ya es JSON:', parsed);
          if (parsed.criterios) {
            return parsed;
          }
        } catch (e) {
          console.log('⚠️ No es JSON válido, procesando como YAML');
        }
      }

      // Implementación simple de parsing YAML para criterios
      const lines = rubricString.split('\n').filter(line => line.trim());
      const criterios: any[] = [];
      let currentCriterio: any = null;

      for (const line of lines) {
        const trimmed = line.trim();
        if (trimmed.startsWith('- nombre:') || trimmed.startsWith('-nombre:')) {
          if (currentCriterio) criterios.push(currentCriterio);
          currentCriterio = {
            nombre: trimmed
              .replace(/^-\s*nombre:\s*/, '')
              .replace(/['"]/g, '')
              .trim(),
            peso: 0,
            descripcion: '',
          };
        } else if (
          (trimmed.startsWith('peso:') || trimmed.startsWith('  peso:')) &&
          currentCriterio
        ) {
          const pesoStr = trimmed.replace(/^\s*peso:\s*/, '').trim();
          currentCriterio.peso = parseInt(pesoStr) || 0;
        } else if (
          (trimmed.startsWith('descripcion:') ||
            trimmed.startsWith('  descripcion:')) &&
          currentCriterio
        ) {
          currentCriterio.descripcion = trimmed
            .replace(/^\s*descripcion:\s*/, '')
            .replace(/['"]/g, '')
            .trim();
        }
      }

      if (currentCriterio) criterios.push(currentCriterio);

      // Si no se encontraron criterios estructurados, crear uno con el texto completo
      if (criterios.length === 0 && rubricString.trim()) {
        criterios.push({
          nombre: 'Descripción de la Rúbrica',
          peso: 100,
          descripcion: rubricString.trim(),
        });
        console.log('📝 Creando criterio desde texto simple:', criterios);
      }

      console.log('✅ Rúbrica parseada:', { criterios });
      return { criterios };
    } catch (error) {
      console.warn('❌ Error parsing rubric:', error);
      return {
        criterios: [
          {
            nombre: 'Error en Rúbrica',
            peso: 100,
            descripcion: 'Hubo un error al procesar la rúbrica',
          },
        ],
      };
    }
  };

  // Función para crear categorías virtuales cuando los datos no están perfectamente estructurados
  const buildHierarchyFromTasks = (tasks: MCPTask[]): TaskHierarchyItem[] => {
    console.log(
      '🔨 buildHierarchyFromTasks llamada con:',
      tasks.length,
      'tasks'
    );
    const categoriesMap = new Map<string, TaskHierarchyItem[]>();

    // Mapeo de categoryId conocidos a nombres legibles
    const categoryIdToName: Record<number, string> = {
      1: 'Comunicación Corporativa',
      2: 'Ventas y Marketing',
      3: 'Finanzas y Reportes',
      8: 'Categoría 8',
      13: 'Categoría 13',
    };

    // Agrupar tasks por categoría
    tasks.forEach(task => {
      // Determinar el nombre de la categoría
      let categoryName = 'Sin Categoría';

      if (task.categoryName) {
        categoryName = task.categoryName;
      } else if (task.categoryId && categoryIdToName[task.categoryId]) {
        categoryName = categoryIdToName[task.categoryId];
      } else if (task.category) {
        categoryName = task.category as string;
      } else if (task.categoryId) {
        categoryName = `Categoría ${task.categoryId}`;
      }

      console.log('📝 Procesando task:', {
        name: task.name,
        categoryId: task.categoryId,
        categoryName: task.categoryName,
        resolvedCategory: categoryName,
      });

      if (!categoriesMap.has(categoryName)) {
        categoriesMap.set(categoryName, []);
      }

      const taskItem: TaskHierarchyItem = {
        id: task.id,
        name: task.name,
        type: 'task',
        description: task.description,
        status: task.status,
        createdAt: task.createdAt,
        lastRun: task.lastRun,
        executions: task.executions,
        successRate: task.successRate,
        rubric: parseRubric(task.rubric),
        children: [],
      };

      categoriesMap.get(categoryName)!.push(taskItem);
    });

    console.log('📊 Categorías creadas:', Array.from(categoriesMap.keys()));

    // Crear estructura jerárquica
    const hierarchy: TaskHierarchyItem[] = [];
    let virtualCategoryId = 1000; // Usar IDs altos para evitar conflictos

    categoriesMap.forEach((taskItems, categoryName) => {
      console.log(
        `📁 Creando categoría "${categoryName}" con ${taskItems.length} tasks`
      );
      hierarchy.push({
        id: `${virtualCategoryId++}`,
        name: categoryName,
        type: 'category',
        children: taskItems,
      });
    });

    console.log('🎯 Jerarquía final buildHierarchyFromTasks:', hierarchy);
    return hierarchy;
  };

  // Construir jerarquía cuando cambien los datos
  useEffect(() => {
    const buildHierarchy = async () => {
      console.log('🏗️ Construyendo jerarquía...', {
        tasksLength: tasks.length,
        tasksLoading,
        sampleTasks: tasks.slice(0, 3),
      });

      setState(prev => ({ ...prev, loading: true, error: null }));

      try {
        let hierarchy: TaskHierarchyItem[] = [];

        // Intentar obtener el árbol jerárquico del backend
        try {
          const treeResponse = await apiService.getTaskTree();
          console.log('🌳 Respuesta del árbol:', {
            categoriesLength: treeResponse?.categories?.length || 0,
          });

          if (treeResponse && treeResponse.categories.length > 0) {
            // Si tenemos árbol jerárquico del backend, usarlo
            hierarchy = treeResponse.categories.map(node =>
              transformTreeNodeToHierarchy(node, tasks)
            );

            // Si ninguna categoría del árbol tiene tasks, también construir desde tasks
            const hasTasksInTree = hierarchy.some(
              category => category.children && category.children.length > 0
            );

            console.log('🔍 Verificando si hay tasks en el árbol:', {
              hasTasksInTree,
              tasksCount: tasks.length,
              hierarchyWithChildren: hierarchy.filter(
                h => h.children && h.children.length > 0
              ).length,
            });

            // Verificar si hay tasks no asignadas a ninguna categoría del árbol
            const tasksInTree = new Set<string>();
            hierarchy.forEach(category => {
              if (category.children) {
                category.children.forEach(child => {
                  if (child.type === 'task') {
                    tasksInTree.add(child.id);
                  }
                });
              }
            });

            // Encontrar tasks huérfanas (que no están en ninguna categoría del árbol)
            const orphanTasks = tasks.filter(task => !tasksInTree.has(task.id));

            console.log(
              '🔍 Tasks huérfanas encontradas:',
              orphanTasks.length,
              'de',
              tasks.length,
              'total'
            );

            if (orphanTasks.length > 0) {
              // Buscar la categoría "OTHER - Others" en la jerarquía
              const otherCategory = hierarchy.find(
                cat => cat.name.includes('OTHER') || cat.name.includes('Others')
              );

              if (otherCategory) {
                console.log(
                  '📁 Asignando',
                  orphanTasks.length,
                  'tasks huérfanas a OTHER'
                );
                // Convertir tasks huérfanas a TaskHierarchyItem
                const orphanTaskItems: TaskHierarchyItem[] = orphanTasks.map(
                  task => ({
                    id: task.id,
                    name: task.name,
                    type: 'task' as const,
                    description: task.description,
                    status: task.status,
                    createdAt: task.createdAt,
                    lastRun: task.lastRun,
                    executions: task.executions,
                    successRate: task.successRate,
                    rubric: parseRubric(task.rubric),
                    children: [],
                  })
                );

                // Agregar las tasks huérfanas a la categoría OTHER
                otherCategory.children = [
                  ...(otherCategory.children || []),
                  ...orphanTaskItems,
                ];
              } else {
                console.log(
                  '📋 No se encontró categoría OTHER, creando jerarquía desde tasks...'
                );
                const taskHierarchy = buildHierarchyFromTasks(orphanTasks);
                // Combinar ambas jerarquías: primero las del árbol, luego las construidas
                hierarchy = [...hierarchy, ...taskHierarchy];
              }
            }
          } else {
            // Si no hay árbol jerárquico, construir uno desde las tasks
            console.log(
              '❌ No hay árbol jerárquico, construyendo desde tasks...'
            );
            hierarchy = buildHierarchyFromTasks(tasks);
          }
        } catch (error) {
          // Si falla la obtención del árbol, construir desde tasks
          console.log(
            '💥 Error obteniendo árbol, construyendo desde tasks:',
            error
          );
          hierarchy = buildHierarchyFromTasks(tasks);
        }

        console.log('✅ Jerarquía final:', hierarchy);
        setState(prev => ({
          ...prev,
          hierarchy,
          loading: false,
        }));
      } catch (error) {
        console.error('💥 Error construyendo jerarquía:', error);
        const apiError =
          error instanceof APIError
            ? error
            : new APIError('Error al construir jerarquía de tasks');

        setState(prev => ({
          ...prev,
          loading: false,
          error: apiError,
        }));
      }
    };

    // Solo construir jerarquía cuando tengamos los datos de tasks
    if (!tasksLoading && tasks.length >= 0) {
      buildHierarchy();
    }
  }, [tasks, tasksLoading, refreshCounter]);

  // Función para refrescar toda la jerarquía
  const refreshHierarchy = useCallback(async () => {
    // Refrescar datos de tasks y categorías
    await Promise.all([refreshTasks(), refreshCategories()]);

    // Forzar reconstrucción de la jerarquía
    setRefreshCounter(prev => prev + 1);
  }, [refreshTasks, refreshCategories]);

  // Función para buscar tasks en la jerarquía
  const searchInHierarchy = useCallback(
    (hierarchy: TaskHierarchyItem[], searchTerm: string): MCPTask[] => {
      const results: MCPTask[] = [];

      const traverse = (items: TaskHierarchyItem[]) => {
        items.forEach(item => {
          if (
            item.type === 'task' &&
            (item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
              item.description
                ?.toLowerCase()
                .includes(searchTerm.toLowerCase()))
          ) {
            // Convertir TaskHierarchyItem a MCPTask para la búsqueda
            results.push({
              id: item.id,
              name: item.name,
              description: item.description || '',
              category: 'General' as any, // Se puede mejorar guardando la categoría padre
              status: item.status as any,
              createdAt: item.createdAt || '',
              lastRun: item.lastRun,
              executions: item.executions || 0,
              successRate: item.successRate || 0,
              rubric: item.rubric ? JSON.stringify(item.rubric) : '',
              enabled: true,
            });
          }

          if (item.children && item.children.length > 0) {
            traverse(item.children);
          }
        });
      };

      traverse(hierarchy);
      return results;
    },
    []
  );

  // Función para obtener todas las categorías para el selector
  const getAllCategoriesFromHierarchy = useCallback(
    (hierarchy: TaskHierarchyItem[]) => {
      const categories: Array<{
        id: string;
        name: string;
        path: string[];
        fullPath: string;
      }> = [];

      const traverse = (items: TaskHierarchyItem[], path: string[] = []) => {
        items.forEach(item => {
          if (item.type === 'category') {
            const fullPath = [...path, item.name];
            categories.push({
              id: item.id,
              name: item.name,
              path: fullPath,
              fullPath: fullPath.join(' > '),
            });

            if (item.children && item.children.length > 0) {
              traverse(item.children, fullPath);
            }
          }
        });
      };

      traverse(hierarchy);
      return categories;
    },
    []
  );

  // Función para aplanar todas las tasks
  const flattenTasks = useCallback(
    (hierarchy: TaskHierarchyItem[]): MCPTask[] => {
      const tasks: MCPTask[] = [];

      const traverse = (items: TaskHierarchyItem[]) => {
        items.forEach(item => {
          if (item.type === 'task') {
            tasks.push({
              id: item.id,
              name: item.name,
              description: item.description || '',
              category: 'General' as any, // Se puede mejorar
              status: item.status as any,
              createdAt: item.createdAt || '',
              lastRun: item.lastRun,
              executions: item.executions || 0,
              successRate: item.successRate || 0,
              rubric: item.rubric ? JSON.stringify(item.rubric) : '',
              enabled: true,
            });
          }

          if (item.children && item.children.length > 0) {
            traverse(item.children);
          }
        });
      };

      traverse(hierarchy);
      return tasks;
    },
    []
  );

  // Determinar estado de loading general
  const loading = tasksLoading || categoriesLoading || state.loading;

  // Determinar error general - solo mostrar error si realmente falla la construcción de jerarquía
  const error =
    state.hierarchy.length === 0 && !loading
      ? tasksError || categoriesError || state.error
      : null;

  return {
    hierarchy: state.hierarchy,
    tasks,
    categories,
    loading,
    error,
    searchInHierarchy,
    getAllCategoriesFromHierarchy,
    flattenTasks,
    refreshHierarchy,
  };
};
